/**
 * Adapter - CardLifeCbsAccountLookup
 *
 * Appelle le backend CardLife pour rechercher un compte via le CBS.
 * Endpoint : POST /api/v1/cbs-integration/clients/balance
 *
 * Le CBS retourne les infos complètes (y compris solde).
 * L'adapter filtre le solde côté frontend pour que le caissier ne le voie pas.
 *
 * IMPORTANT: L'API CBS /v3/solde exige un codeuser (code agent/caissier).
 * Ce code est récupéré depuis VITE_AGENT_CODE dans .env.
 */

import { ICbsAccountLookup, CbsAccountInfo } from '../../application/ports/output/ICbsAccountLookup';

const ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const CARDLIFE_BASE_URL = (ENV.VITE_CARDLIFE_BASE_URL ?? '').replace(/\/+$/g, '');
const BALANCE_ENDPOINT = '/api/v1/cbs-integration/clients/balance';
const AGENT_CODE = ENV.VITE_AGENT_CODE ?? '';

// Debug: vérifier que VITE_AGENT_CODE est chargé
console.log('[CbsLookup] VITE_AGENT_CODE chargé:', AGENT_CODE ? `"${AGENT_CODE}"` : '⚠️ VIDE — redémarrez le serveur Vite');

export class CardLifeCbsAccountLookup implements ICbsAccountLookup {
  async searchAccount(numcpt: string, codeuser?: string): Promise<CbsAccountInfo | null> {
    const url = `${CARDLIFE_BASE_URL}${BALANCE_ENDPOINT}`;
    const reference = `SEARCH-${Date.now()}`;

    // Utiliser le codeuser fourni, sinon le code agent configuré dans .env
    const effectiveCodeuser = (codeuser && codeuser.trim()) || AGENT_CODE;

    if (!effectiveCodeuser) {
      console.error('[CbsLookup] Aucun code agent configuré (VITE_AGENT_CODE)');
      throw new Error('Code agent non configuré. Vérifiez VITE_AGENT_CODE dans .env');
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          id: '',
          numcpt: numcpt.trim(),
          reference,
          codeope: '06810',
          codeuser: effectiveCodeuser,
        }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorText = await response.text();
        console.error('[CbsLookup] Erreur:', response.status, errorText);
        throw new Error(`Erreur CBS: ${response.status}`);
      }

      const data = await response.json();

      // Vérifier le code réponse CBS
      if (data.response !== '0') {
        console.warn('[CbsLookup] Compte non trouvé:', data.message);
        return null;
      }

      // IMPORTANT: On retourne uniquement les infos SANS le solde
      // Le caissier ne doit jamais voir le solde du client
      return {
        numcpt: data.numcpt,
        intitule: data.intitule,
        codeuser: data.codeuser,
      };
    } catch (error) {
      console.error('[CbsLookup] Erreur recherche compte:', error);
      throw error;
    }
  }
}
